import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useCatalogue } from './EcaPluginCatalogueContext.jsx';

// Per-type visual identity — colours match node header colours
const TYPE_META = {
  event: {
    label: 'Event',
    pill: 'bg-iris-100 text-iris-700 border-iris-200',
    group: 'text-iris-600 border-iris-200 bg-iris-50',
    item: 'hover:bg-iris-50 hover:border-iris-200',
  },
  condition: {
    label: 'Condition',
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
    group: 'text-amber-600 border-amber-200 bg-amber-50',
    item: 'hover:bg-amber-50 hover:border-amber-200',
  },
  action: {
    label: 'Action',
    pill: 'bg-blue-100 text-blue-700 border-blue-200',
    group: 'text-blue-600 border-blue-200 bg-blue-50',
    item: 'hover:bg-blue-50 hover:border-blue-200',
  },
  gateway: {
    label: 'Gateway',
    pill: 'bg-rose-100 text-rose-700 border-rose-200',
    group: 'text-rose-600 border-rose-200 bg-rose-50',
    item: 'hover:bg-rose-50 hover:border-rose-200',
  },
};

const SUGGESTIONS = [
  { keyword: 'entity', hint: 'charger, modifier ou conditionner des entités Drupal' },
  { keyword: 'token', hint: 'passer des valeurs entre nodes via des tokens' },
  { keyword: 'mail', hint: 'envoyer un e-mail depuis un workflow' },
  { keyword: 'cron', hint: 'déclencher un workflow depuis le planificateur' },
];

// ---------------------------------------------------------------------------
// Fuzzy search engine
// ---------------------------------------------------------------------------

/**
 * Subsequence (fuzzy) score of `query` against `text`.
 * Returns -1 if the query is not a subsequence of text.
 * Higher score = better match. Bonuses for:
 *   - exact substring (very high)
 *   - consecutive character runs
 *   - matches near the start of the string
 *
 * NOTE — the app's shared fuzzy engine lives in `utils/fuzzy.js` (`fuzzyScore`).
 * This picker keeps its OWN variant on purpose: it is coupled to per-character
 * match highlighting (`matchIndices`, below) and weighted multi-field scoring
 * with tuned magnitudes (`scorePlugin`) — behaviour the shared scorer does not
 * provide. Don't replace it without porting the highlighting.
 */
function fuzzyScore(query, text) {
  if (!query || !text) return -1;
  const q = query.toLowerCase();
  const t = text.toLowerCase();

  // Exact substring — top priority
  const exactIdx = t.indexOf(q);
  if (exactIdx !== -1) return 10000 - exactIdx;

  // Subsequence matching
  let qi = 0;
  let score = 0;
  let run = 0;      // length of current consecutive-char run
  let last = -1;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      run = last === ti - 1 ? run + 1 : 1;
      score += run * 8;                     // consecutive-run bonus
      score += Math.max(0, 40 - ti);        // near-start bonus
      last = ti;
      qi++;
    }
  }

  return qi === q.length ? score : -1;
}

/**
 * Return the indices in `text` that were matched by `word` (fuzzy/exact).
 * Returns null if word is not a subsequence of text.
 */
function matchIndices(word, text) {
  if (!word || !text) return null;
  const w = word.toLowerCase();
  const t = text.toLowerCase();

  // Exact substring → contiguous range
  const exactIdx = t.indexOf(w);
  if (exactIdx !== -1) return Array.from({ length: w.length }, (_, i) => exactIdx + i);

  // Subsequence
  const indices = [];
  let wi = 0;
  for (let ti = 0; ti < t.length && wi < w.length; ti++) {
    if (t[ti] === w[wi]) { indices.push(ti); wi++; }
  }
  return wi === w.length ? indices : null;
}

/**
 * Union of matched indices across all words of `query` against `text`.
 * Returns a sorted Set of matched character positions.
 */
function allMatchIndices(query, text) {
  const words = query.trim().split(/\s+/).filter(Boolean);
  const set = new Set();
  for (const word of words) {
    (matchIndices(word, text) ?? []).forEach(i => set.add(i));
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Score a plugin against a multi-word query.
 * Every word must match at least one field; the best matching field per word
 * is taken. Returns -1 if any word has no match at all.
 */
function scorePlugin(plugin, query) {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return -1;

  let total = 0;

  for (const word of words) {
    const candidates = [
      fuzzyScore(word, plugin.label ?? '')       * 4,  // label: highest weight
      fuzzyScore(word, plugin.id ?? '')           * 2,  // plugin id
      fuzzyScore(word, TYPE_META[plugin.nodeType]?.label ?? '') * 2,
      fuzzyScore(word, plugin.description ?? '')  * 1,  // description: lowest weight
      fuzzyScore(word, plugin.category ?? '')     * 1,
    ].filter(s => s > 0);

    if (!candidates.length) return -1;         // word must match somewhere
    total += Math.max(...candidates);
  }

  return total;
}

// Gateways are internal ECA constructs, not user-facing plugin types.
const TYPE_FILTERS = ['event', 'condition', 'action'];

/**
 * Left sidebar — node catalogue.
 * Shows nothing until the user types a search query.
 * Results are a flat list sorted by relevance score.
 * Type toggle buttons allow filtering by node type.
 *
 * Performance: useDeferredValue defers the expensive scoring pass so the
 * input stays responsive while results update in the background.
 */
export const EcaPluginPicker = ({ onAddNode }) => {
  const [search, setSearch] = useState('');
  // Plain array avoids React Set-comparison pitfalls; empty = show all types.
  const [activeTypes, setActiveTypes] = useState([]);
  const { catalogue, loading } = useCatalogue();

  const allPlugins = useMemo(() => {
    const raw = [
      ...(catalogue.events     ?? []).map((p) => ({ ...p, nodeType: 'event' })),
      ...(catalogue.conditions ?? []).map((p) => ({ ...p, nodeType: 'condition' })),
      ...(catalogue.actions    ?? []).map((p) => ({ ...p, nodeType: 'action' })),
    ];
    // Deduplicate by composite key (same plugin id can appear in multiple categories).
    const seen = new Set();
    return raw.filter(p => {
      const k = `${p.nodeType}:${p.id}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [catalogue]);

  const toggleType = (type) =>
    setActiveTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );

  const q = search.trim();

  // Debounce: wait 220 ms after the last keystroke before updating the query
  // used for scoring, so the input stays snappy and avoids redundant passes.
  const [debouncedQ, setDebouncedQ] = useState(q);
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(q), 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Defer the scoring render pass to keep the UI responsive during computation.
  const deferredQ     = useDeferredValue(debouncedQ);
  const deferredTypes = useDeferredValue(activeTypes);

  const filteredPlugins = useMemo(() => {
    if (!deferredQ) return [];
    return allPlugins
      .map(p => ({ ...p, _score: scorePlugin(p, deferredQ) }))
      .filter(p =>
        p._score > 0 &&
        (deferredTypes.length === 0 || deferredTypes.includes(p.nodeType))
      )
      .sort((a, b) => b._score - a._score);
  }, [allPlugins, deferredQ, deferredTypes]);

  // Show a subtle "computing…" hint while debounce/deferred values lag behind.
  const isStale = deferredQ !== debouncedQ || deferredTypes !== activeTypes;

  const handleDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/eca-node', JSON.stringify(item));
  };

  const handleClick = (item) => onAddNode?.(item);

  const isSearching = debouncedQ.length >= 1;

  return (
    <div className="w-60 bg-white border-r border-gray-200 flex flex-col h-full shadow-sm">
      {/* Header */}
      <div className="px-3 py-3 border-b border-gray-100 space-y-2">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nodes</h2>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un plugin…"
          className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-300"
        />
        {/* Type filter toggles */}
        <div className="flex flex-wrap gap-1">
          {TYPE_FILTERS.map(type => {
            const meta = TYPE_META[type];
            const active = activeTypes.includes(type);
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={`text-xs px-1.5 py-0.5 rounded border font-semibold transition-colors ${
                  active ? meta.pill : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-500'
                }`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 flex flex-col items-center gap-2 text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-400 rounded-full animate-spin" />
            <p className="text-xs">Chargement des plugins…</p>
          </div>
        ) : isSearching ? (
          /* ── Flat results sorted by relevance score ───────────────── */
          <div className={`py-2 px-2 space-y-1 transition-opacity ${isStale ? 'opacity-50' : 'opacity-100'}`}>
            {filteredPlugins.length === 0 && !isStale && (
              <p className="text-xs text-gray-400 px-2 py-6 text-center leading-relaxed">
                Aucun node trouvé pour{' '}
                <span className="font-medium text-gray-600">"{search}"</span>
                {activeTypes.length > 0 && (
                  <span className="block mt-1 text-gray-300">
                    (filtre actif : {activeTypes.join(', ')})
                  </span>
                )}
              </p>
            )}
            {filteredPlugins.map((plugin) => (
              <PickerItem
                key={`${plugin.nodeType}:${plugin.id}`}
                label={plugin.label}
                pluginId={plugin.id}
                description={plugin.description}
                nodeType={plugin.nodeType}
                query={q}
                onDragStart={(e) =>
                  handleDragStart(e, {
                    nodeType: plugin.nodeType,
                    pluginId: plugin.id,
                    label: plugin.label,
                  })
                }
                onClick={() =>
                  handleClick({
                    nodeType: plugin.nodeType,
                    pluginId: plugin.id,
                    label: plugin.label,
                  })
                }
              />
            ))}
          </div>
        ) : (
          /* ── Browse navigation (hierarchical) ─────────────────────── */
          <>
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Suggestions</p>
              <div className="flex flex-wrap gap-1">
                {SUGGESTIONS.map(({ keyword }) => (
                  <button
                    key={keyword}
                    onClick={() => setSearch(keyword)}
                    className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            </div>
            <BrowseNav allPlugins={allPlugins} onAddNode={onAddNode} />
          </>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-gray-100">
        <p className="text-xs text-gray-300 text-center">Glisser ou cliquer pour ajouter</p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Browse navigation — hierarchical explorer (shown when no search query)
// ---------------------------------------------------------------------------

/** Convert a Drupal module machine name to a human-readable label. */
const humanizeProvider = (p) =>
  p ? p.replace(/^eca_/, 'ECA ').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Autre';

const NavRow = ({ label, count, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
  >
    <span>{label}</span>
    <span className="flex items-center gap-1 shrink-0 text-gray-400">
      <span className="tabular-nums text-gray-300">{count}</span>
      <span>›</span>
    </span>
  </button>
);

const NavBackButton = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50 border-b border-gray-100 transition-colors"
  >
    <span className="text-gray-400 text-base leading-none">‹</span>
    <span>{label}</span>
  </button>
);

/**
 * Hierarchical plugin browser.
 * navStack: [] → [nodeType] → [nodeType, provider] → [nodeType, provider, category]
 */
const BrowseNav = ({ allPlugins, onAddNode }) => {
  const [navStack, setNavStack] = useState([]);
  const push = (seg) => setNavStack(prev => [...prev, seg]);
  const pop  = ()    => setNavStack(prev => prev.slice(0, -1));

  const mkItem = (plugin) => {
    const item = { nodeType: plugin.nodeType, pluginId: plugin.id, label: plugin.label };
    return (
      <PickerItem
        key={`${plugin.nodeType}:${plugin.id}`}
        label={plugin.label}
        pluginId={plugin.id}
        description={plugin.description}
        nodeType={plugin.nodeType}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('application/eca-node', JSON.stringify(item));
        }}
        onClick={() => onAddNode?.(item)}
      />
    );
  };

  // ── Level 1: type selection ──────────────────────────────────────────────
  if (navStack.length === 0) {
    return (
      <div>
        {['event', 'condition', 'action'].map(type => {
          const meta  = TYPE_META[type];
          const count = allPlugins.filter(p => p.nodeType === type).length;
          return (
            <button
              key={type}
              onClick={() => push(type)}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold hover:bg-gray-50 active:bg-gray-100 transition-colors ${meta.item}`}
            >
              <span className={`px-1.5 py-0 rounded border ${meta.pill}`}>{meta.label}</span>
              <span className="flex items-center gap-1 shrink-0">
                <span className="tabular-nums font-normal text-gray-300">{count}</span>
                <span className="text-gray-300">›</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  const nodeType       = navStack[0];
  const meta           = TYPE_META[nodeType];
  const pluginsOfType  = allPlugins.filter(p => p.nodeType === nodeType);

  // ── Level 2: provider (module) selection ────────────────────────────────
  if (navStack.length === 1) {
    const providers = [...new Set(pluginsOfType.map(p => p.provider ?? ''))].sort();
    return (
      <div>
        <NavBackButton label={meta.label} onClick={pop} />
        {providers.map(prov => (
          <NavRow
            key={prov || '_none'}
            label={humanizeProvider(prov)}
            count={pluginsOfType.filter(p => (p.provider ?? '') === prov).length}
            onClick={() => push(prov)}
          />
        ))}
      </div>
    );
  }

  const provider         = navStack[1];
  const pluginsOfProv    = pluginsOfType.filter(p => (p.provider ?? '') === provider);
  // The backend uses provider as a fallback category when no real category exists.
  // Exclude that case so those plugins appear directly at this level.
  const categories       = [...new Set(pluginsOfProv.map(p => p.category ?? '').filter(
    c => Boolean(c) && c !== provider
  ))].sort();
  const uncategorized    = pluginsOfProv.filter(p => !p.category || p.category === provider);

  // ── Level 3: category selection or direct list ──────────────────────────
  if (navStack.length === 2) {
    if (categories.length === 0) {
      return (
        <div>
          <NavBackButton label={humanizeProvider(provider)} onClick={pop} />
          <div className="px-2 pt-1 space-y-1">{pluginsOfProv.map(mkItem)}</div>
        </div>
      );
    }
    return (
      <div>
        <NavBackButton label={humanizeProvider(provider)} onClick={pop} />
        {categories.map(cat => (
          <NavRow
            key={cat}
            label={cat}
            count={pluginsOfProv.filter(p => p.category === cat).length}
            onClick={() => push(cat)}
          />
        ))}
        {uncategorized.length > 0 && (
          <>
            <div className="mx-3 my-1 border-t border-gray-100" />
            <div className="px-2 space-y-1">{uncategorized.map(mkItem)}</div>
          </>
        )}
      </div>
    );
  }

  // ── Level 4: plugins in selected category ────────────────────────────────
  const category           = navStack[2];
  const pluginsInCategory  = pluginsOfProv.filter(p => p.category === category);
  return (
    <div>
      <NavBackButton label={category} onClick={pop} />
      <div className="px-2 pt-1 space-y-1">{pluginsInCategory.map(mkItem)}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// HighlightedText
// ---------------------------------------------------------------------------

/** Renders `text` with characters at `indices` highlighted. */
const HighlightedText = ({ text, query, className = '' }) => {
  if (!query || !text) return <span className={className}>{text}</span>;
  const indices = new Set(allMatchIndices(query, text));
  if (!indices.size) return <span className={className}>{text}</span>;

  return (
    <span className={className}>
      {[...text].map((char, i) =>
        indices.has(i)
          ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded-[2px] not-italic">{char}</mark>
          : <span key={i}>{char}</span>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// PickerItem
// ---------------------------------------------------------------------------

const PickerItem = ({ label, pluginId, description, nodeType, query, onDragStart, onClick }) => {
  const meta = TYPE_META[nodeType] ?? TYPE_META.action;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className={`flex flex-col gap-1 px-2 py-2 rounded border border-gray-100 bg-gray-50 cursor-grab active:cursor-grabbing transition-colors ${meta.item}`}
    >
      {/* Type pill + label */}
      <div className="flex items-center gap-1.5">
        <span className={`text-xs px-1.5 py-0 rounded border font-semibold shrink-0 ${meta.pill}`}>
          {meta.label}
        </span>
        <HighlightedText
          text={label}
          query={query}
          className="text-xs font-medium text-gray-700 truncate"
        />
      </div>
      {/* Description */}
      {description && (
        <p className="text-xs text-gray-500 leading-tight line-clamp-2 pl-0.5">
          <HighlightedText text={description} query={query} />
        </p>
      )}
      {/* Plugin ID */}
      {pluginId && (
        <HighlightedText
          text={pluginId}
          query={query}
          className="text-xs text-gray-300 font-mono leading-tight truncate pl-0.5"
        />
      )}
    </div>
  );
};
